import type { SupabaseClient } from "@supabase/supabase-js";
import { SOS_QUEUE_LIMIT, isStandDownOutcome, type StandDownOutcomeKey } from "./sos-desk";

// S1 complete-or-unavailable contract. It deliberately requires no S2 lead,
// version, note-history, or callerIsLead fields and does not invent those facts.
export interface ResponseOnlySignal {
  alertEventId: string;
  status: "open" | "acknowledged" | "resolved" | "superseded";
  guardLabel: string | null;
  siteName: string | null;
  routeName: string | null;
  responseCount: number;
  callerAcknowledged: boolean;
  standDownOutcome: StandDownOutcomeKey | null;
}
export type ResponseOnlyRead =
  | { status: "ok"; rows: ResponseOnlySignal[] }
  | { status: "empty" }
  | { status: "unavailable"; reason: "capped" | "unreadable" };

const unreadable = (): ResponseOnlyRead => ({ status: "unavailable", reason: "unreadable" });
const text = (value: unknown): string | null => typeof value === "string" && value.length > 0 ? value : null;
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export async function fetchSosCreationState(client: SupabaseClient, accountId: string): Promise<boolean> {
  const { data, error } = await client.rpc("get_mobile_client_flags", { active_account_id: accountId });
  if (error !== null || typeof data !== "object" || data === null || Array.isArray(data) ||
      typeof (data as Record<string, unknown>).mobile_sos !== "boolean") {
    throw new Error("Could not verify SOS creation policy.");
  }
  return (data as { mobile_sos: boolean }).mobile_sos;
}

export function readResponseOnlyEnvelope(raw: unknown): ResponseOnlyRead {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return unreadable();
  const envelope = raw as Record<string, unknown>;
  if (envelope.status === "empty") {
    if (Object.keys(envelope).some(key => key !== "status" && key !== "rows") ||
        ("rows" in envelope && (!Array.isArray(envelope.rows) || envelope.rows.length !== 0))) return unreadable();
    return { status: "empty" };
  }
  if (envelope.status !== "ok") return envelope.reason === "too_many"
    ? { status: "unavailable", reason: "capped" } : unreadable();
  if (!Array.isArray(envelope.rows) || envelope.rows.length === 0) return unreadable();
  if (envelope.rows.length > SOS_QUEUE_LIMIT) return { status: "unavailable", reason: "capped" };
  const rows: ResponseOnlySignal[] = [], seen = new Set<string>();
  for (const value of envelope.rows) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) return unreadable();
    const row = value as Record<string, unknown>;
    const id = text(row.alertEventId), status = text(row.status);
    if (id === null || !uuid.test(id) || seen.has(id) || row.alertType !== "sos" ||
        status === null || !["open", "acknowledged", "resolved", "superseded"].includes(status) ||
        typeof row.responseCount !== "number" || !Number.isSafeInteger(row.responseCount) || row.responseCount < 0 ||
        typeof row.callerAcknowledged !== "boolean") return unreadable();
    for (const key of ["guardLabel", "siteName", "routeName", "standDownOutcome"]) {
      if (row[key] !== null && typeof row[key] !== "string") return unreadable();
    }
    const outcome = text(row.standDownOutcome);
    if (outcome !== null && !isStandDownOutcome(outcome)) return unreadable();
    seen.add(id);
    rows.push({
      alertEventId: id, status: status as ResponseOnlySignal["status"],
      guardLabel: text(row.guardLabel), siteName: text(row.siteName), routeName: text(row.routeName),
      responseCount: row.responseCount, callerAcknowledged: row.callerAcknowledged,
      standDownOutcome: outcome,
    });
  }
  return { status: "ok", rows };
}

export async function fetchResponseOnlySignals(client: SupabaseClient, accountId: string): Promise<ResponseOnlyRead> {
  try {
    const { data, error } = await client.rpc("management_sos_signal_queue", { p_account_id: accountId });
    return error === null ? readResponseOnlyEnvelope(data) : unreadable();
  } catch { return unreadable(); }
}
